import { useCreateDataScienceTaskMutation } from 'app/apis/commandApi';
import { useGetDataScienceTaskDefaultParametersQuery } from 'app/apis/queryApi';
import { RootState } from 'app/store';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import Textbox from 'common/components/inputs/textbox';
import Spinner from 'common/components/loading/spinner';
import { is } from 'date-fns/locale';
import { DataScienceFeatures } from 'features/data-science/task-features';
import { DataScienceTaskParametersDto } from 'features/data-science/tasks.models';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { onSuccess } from 'features/toast/toast.slice';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
const dataScienceFeatureTitles: Record<string, string> = {
  [DataScienceFeatures.HybridImpressions]: 'DDA & Impressions'
};

const SettingsCreateDataScienceTaskPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [targetDataScienceFeature, updateTargetDataScienceFeature] =
    useState<DataScienceFeatures | null>(null);

  const [
    autoDeployModelOnTrainingComplete,
    setAutoDeployModelOnTrainingComplete
  ] = useState<boolean>(false);

  const resetChosenFeature = () => {
    updateTargetDataScienceFeature(null);
  };

  const [dataScienceTaskModelParameters, setDataScienceTaskModelParameters] =
    useState<Record<string, string> | null>(null);

  const setDataScienceTaskModelParameter = (key: string, value: string) => {
    setDataScienceTaskModelParameters((prev) => ({
      ...(prev ?? {}),
      [key]: value
    }));
  };

  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const { data: dataScienceTaskDefaultParametersData, isFetching } =
    useGetDataScienceTaskDefaultParametersQuery(
      {
        siteId,
        dataScienceFeature: targetDataScienceFeature as DataScienceFeatures
      },
      {
        skip: targetDataScienceFeature == null
      }
    );

  const modelTrainingPipelineParameters = useMemo(() => {
    if (dataScienceTaskDefaultParametersData?.modelTrainingPipelineParameters) {
      return Object.entries(
        dataScienceTaskDefaultParametersData.modelTrainingPipelineParameters
      ).filter(([key, _]) => key !== 'SiteId');
    }
    return [];
  }, [dataScienceTaskDefaultParametersData]);

  const [createDataScienceTask, { isLoading }] =
    useCreateDataScienceTaskMutation();

  const onCreateDataScienceTask = async () => {
    if (targetDataScienceFeature == null) {
      console.error('No target data science feature selected');
      return;
    }
    try {
      await createDataScienceTask({
        siteId,
        dataScienceTaskRequest: {
          machineLearningModel: targetDataScienceFeature,
          autoDeployModelOnTrainingComplete,
          modelTrainingPipelineParameters: dataScienceTaskModelParameters ?? {}
        }
      }).unwrap();

      dispatch(
        onSuccess({
          title: 'Data Science Task Created',
          description: `The ${targetDataScienceFeature} task has successfully been created!`
        })
      );

      navigate('/settings/data-science/tasks');
    } catch (error) {
      console.error('Failed to create data science task:', error);
    }
  };

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <h2 className="text-primary-700 text-l leading-7 pb-5 mb-3 mt-2 border-b border-neutral-200 ">
            Create Data Science Task
          </h2>
          {targetDataScienceFeature != null ? (
            <>
              <button onClick={resetChosenFeature}>
                <span className="flex items-center">
                  <p>Back to feature selection.</p>
                  <i className="ra-icons-generic-forward text-neutral-500 hover:text-primary-50 hover:border-primary-50 transition-all flex items-center w-fit text-sm ml-2"></i>
                </span>
              </button>
              <p className="mt-4">
                You have selected the{' '}
                {dataScienceFeatureTitles[targetDataScienceFeature]} feature.
                <br />
                Please fill out the form below to create a new data science
                task:
                <br />
              </p>
              {!isFetching && (
                <div key="autoDeployModelOnTrainingComplete" className="mt-4">
                  <label
                    htmlFor="autoDeployModelOnTrainingComplete"
                    className="block mb-1 text-sm"
                  >
                    {'AutoDeployModelOnTrainingComplete'
                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                      .replace(/_/g, ' ')}
                    :
                  </label>
                  <Textbox
                    errors={[]}
                    identifier="autoDeployModelOnTrainingComplete"
                    value={`${autoDeployModelOnTrainingComplete ?? ''}`}
                    placeholder="false"
                    onChange={(value) => {
                      setAutoDeployModelOnTrainingComplete(
                        value.toLowerCase() === 'true'
                      );
                    }}
                  />
                </div>
              )}
              {!isFetching &&
              modelTrainingPipelineParameters &&
              modelTrainingPipelineParameters.length > 0 ? (
                <>
                  {modelTrainingPipelineParameters.map(([key, value]) => {
                    return (
                      <div key={key} className="mt-4">
                        <label htmlFor={key} className="block mb-1 text-sm">
                          {key
                            .replace(/([a-z])([A-Z])/g, '$1 $2')
                            .replace(/_/g, ' ')}
                          :
                        </label>
                        <Textbox
                          errors={[]}
                          identifier={key}
                          value={dataScienceTaskModelParameters?.[key] ?? ''}
                          placeholder={
                            value === undefined ? 'No default value' : value
                          }
                          onChange={(value) => {
                            setDataScienceTaskModelParameter(key, value);
                          }}
                        />
                      </div>
                    );
                  })}
                  <br />
                  <Button
                    onClick={async () => await onCreateDataScienceTask()}
                    theme={ButtonTheme.CREATE}
                    identifier="create-data-science-task"
                    isLoading={isLoading}
                  >
                    <p>Create</p>
                  </Button>
                </>
              ) : (
                <>
                  {isFetching ? (
                    <>
                      <br />
                      <div className="flex justify-center">
                        <Spinner />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-neutral-500">
                      No controllable parameters available for this feature.
                      Click create below to use system defaults.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <div>
              <p>
                This is the page to create a new data science task. Please
                select what task you wish to create:
              </p>

              <div className="flex flex-col gap-2 mt-4">
                {Object.entries(dataScienceFeatureTitles).map(
                  ([key, value]) => (
                    <Button
                      identifier="data-science-task-feature-button"
                      key={key}
                      classOverrides="w-60"
                      theme={ButtonTheme.PRIMARY}
                      onClick={() =>
                        updateTargetDataScienceFeature(
                          key as DataScienceFeatures
                        )
                      }
                    >
                      <span>{value}</span>
                    </Button>
                  )
                )}
              </div>
            </div>
          )}
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};

export default SettingsCreateDataScienceTaskPage;
